import React, { useState } from 'react';
import { Modal, Input, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CancelButton, CreateButton } from '../../buttons';
import * as S from './CreateClassModal.styles';

const { Option } = Select;

export interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; year: string }) => void;
  isLoading?: boolean;
}

export const CreateClassModal: React.FC<CreateClassModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const currentYear = new Date().getFullYear();
  const defaultYear = `${currentYear}-${currentYear + 1}`;
  
  const [formData, setFormData] = useState({
    name: '',
    year: defaultYear
  });
  
  // Generate year options (current year - 2 to current year + 5)
  const yearOptions = [];
  for (let i = currentYear - 2; i <= currentYear + 5; i++) {
    yearOptions.push({
      value: `${i}-${i + 1}`,
      label: `${i}-${i + 1}`
    });
  }

  const handleYearChange = (value: string) => {
    setFormData(prev => ({ ...prev, year: value }));
  };

  const handleSubmit = () => {
    if (formData.name.trim() && formData.year) {
      onSubmit({
        name: formData.name.trim(),
        year: formData.year
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ name: '', year: defaultYear });
      onClose();
    }
  };

  const isFormValid = formData.name.trim().length > 0 && formData.year.length > 0;

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      width={400}
      closable={false}
      maskClosable={!isLoading}
    >
      <S.ModalContent>
        <S.Header>
          <S.IconContainer>
            <S.CreateIcon>
              <PlusOutlined />
            </S.CreateIcon>
          </S.IconContainer>
          <S.Title>Create a new class</S.Title>
          <S.Subtitle>Fill in the details to create your new class</S.Subtitle>
        </S.Header>

        <S.FormSection>
          <S.FormField>
            <S.Label>Class name</S.Label>
            <S.StyledInput
              placeholder="e.g. Grade 7A — Mathematics"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isLoading}
              size="large"
            />
          </S.FormField>

          <S.FormField>
            <S.Label>Academic year</S.Label>
            <S.StyledSelect
              placeholder="Select academic year"
              onChange={handleYearChange}
              disabled={isLoading}
              size="large"
              value={formData.year || undefined}
            >
              {yearOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </S.StyledSelect>
            {formData.year && (
              <S.YearDisplay>Academic year: {formData.year}</S.YearDisplay>
            )}
          </S.FormField>
        </S.FormSection>

        <S.ButtonSection>
          <CancelButton 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CancelButton>
          
          <CreateButton 
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid}
            loading={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create'}
          </CreateButton>
        </S.ButtonSection>
      </S.ModalContent>
    </Modal>
  );
};
